/**
 * VoiceButton — wave-icon voice-call entry in the chat input row.
 * Respects VITE_ENABLE_VOICE feature flag (returns null if unset).
 * Filled-indigo round button when idle; red with pulsing ring when active.
 */
import { useTranslation } from 'react-i18next';
import { AudioLines, MicOff, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { VOICE_ENABLED } from '@/lib/featureFlags';
import type { VoiceState } from '@core/voice/pip';

/**
 * The core PiP machine's states (#1731: idle · requesting · connecting ·
 * listening · speaking · ended · failed) plus the legacy panel's, which the
 * TRFer page keeps until phase 9.
 */
export type VoiceButtonState = VoiceState | 'connected' | 'disconnected' | 'error';

interface VoiceButtonProps {
  onStart: () => void;
  onStop: () => void;
  isActive: boolean;
  state: VoiceButtonState;
  disabled?: boolean;
}

export function VoiceButton({ onStart, onStop, isActive, state, disabled }: VoiceButtonProps) {
  const { t } = useTranslation('chat');
  if (!VOICE_ENABLED) return null;

  // Mic first, mint second: both are "connecting" to the person pressing it.
  const isConnecting = state === 'connecting' || state === 'requesting';
  const label = isActive ? t('voiceButton.end') : t('voiceButton.start');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={disabled || isConnecting}
          onClick={isActive ? onStop : onStart}
          className={cn(
            'relative flex-shrink-0 inline-flex items-center justify-center h-[var(--chat-control-size)] w-[var(--chat-control-size)] rounded-full transition-colors',
            isActive
              ? 'bg-status-danger-solid text-status-danger-solid-fg hover:brightness-110'
              : 'bg-stevens-indigo-solid text-stevens-indigo-solid-fg hover:bg-stevens-indigo-solid/90',
            (state === 'error' || state === 'failed') && 'bg-destructive',
            (disabled || isConnecting) && 'opacity-60 cursor-not-allowed'
          )}
          aria-label={label}
          aria-pressed={isActive}
        >
          {isActive && !isConnecting && (
            <span className="absolute inset-0 rounded-full animate-ping bg-status-danger-fg opacity-30" />
          )}

          {isConnecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isActive ? (
            <MicOff className="h-4 w-4" />
          ) : (
            <AudioLines className="h-4 w-4" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
